/* eslint-disable max-lines */
import { fetchNotifications } from 'actions/notifications';
import LinkLookup from 'components/LinkLookup';
import useClickOutside from 'components/utils/useClickOutside';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getNotifications } from 'selectors/notifications';
import { useSelector } from 'store';
import NotificationItem from './NotificationItem';

const NotificationsCenter: React.FC = () => {
  const dispatch = useDispatch();

  const notifications = useSelector(getNotifications);

  const [isOpen, setIsOpen] = useState(false);

  // click outside functionality
  const handleClickOutside = useCallback(() => setIsOpen(false), [setIsOpen]);
  const wrapperRef = useRef(null);
  useClickOutside(wrapperRef, handleClickOutside);

  useEffect(() => {
    dispatch(fetchNotifications({ order_by: 'read,created_at DESC' }));
  }, [dispatch]);

  const toggleMenu = (): void => {
    setIsOpen((b) => !b);
  };

  return (
    <div
      className={`h-full pt-2 flex items-center justify-center border-b-4 ${
        isOpen ? 'border-litlingo-secondary-100' : 'border-transparent'
      }`}
    >
      <div ref={wrapperRef} className="relative w-11 pl-2.5">
        <button
          type="button"
          className="self-center focus:outline-none"
          aria-label="Notifications"
          onClick={toggleMenu}
        >
          <svg
            width="24"
            height="27"
            viewBox="0 0 24 27"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15.6179 23.8142C15.4078 25.6079 13.8824 27 12.0321 27C10.1817 27 8.65632 25.6079 8.44628 23.8142H2.26335C1.67549 23.8142 1.2002 23.3401 1.2002 22.7534V21.4779C1.2002 20.1887 2.24735 19.1415 3.53738 19.1415C3.65293 19.1415 3.74894 19.0455 3.74894 18.9285V13.8316C3.74894 10.1984 6.106 7.0371 9.48328 5.94816V5.54904C9.48328 4.14189 10.6258 3 12.0321 3C13.4404 3 14.5809 4.13954 14.5809 5.54904V5.94816C17.9579 7.03733 20.3152 10.1989 20.3152 13.8316V18.9285C20.3152 19.0479 20.4089 19.1415 20.5268 19.1415C21.8148 19.1415 22.864 20.1884 22.864 21.4779V22.7534C22.864 23.3375 22.3869 23.8142 21.8008 23.8142H15.6179ZM14.3299 23.8142H9.73427C9.93398 24.9016 10.8869 25.7256 12.0321 25.7256C13.1773 25.7256 14.1302 24.9016 14.3299 23.8142ZM21.5896 21.4778C21.5896 20.8929 21.1116 20.4159 20.5267 20.4159C19.7051 20.4159 19.0408 19.7519 19.0408 18.9285V13.8315C19.0408 10.6149 16.8558 7.83383 13.7847 7.0436L13.3064 6.92063V5.54906C13.3064 4.84356 12.737 4.27435 12.032 4.27435C11.3295 4.27435 10.7576 4.84591 10.7576 5.54906V6.92063L10.2794 7.0436C7.20826 7.83383 5.02331 10.614 5.02331 13.8315V18.9285C5.02331 19.7492 4.35693 20.4159 3.53735 20.4159C2.95097 20.4159 2.4745 20.8926 2.4745 21.4778V22.5398H21.5895L21.5896 21.4778Z"
              fill="white"
            />
            {notifications.some((n) => !n.read) && (
              <rect x="14" width="10" height="10" rx="5" fill="#E20A0A" />
            )}
          </svg>
        </button>

        {isOpen && (
          <div
            data-testid="dropdown-menu"
            className="absolute right-0 mt-5 w-63 rounded shadow-lg z-50 text-small"
          >
            <div
              className="rounded bg-white shadow-xs overflow-visible custom-scrollbar"
              style={{ filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))' }}
            >
              <div
                role="menu"
                data-testid="options-container"
                aria-orientation="vertical"
                aria-labelledby="options-menu"
                className="rounded"
              >
                {notifications.slice(0, 5).map((notification, index) => (
                  <NotificationItem
                    key={notification.uuid}
                    notification={notification}
                    index={index}
                  />
                ))}
                <LinkLookup
                  routeName="notifications"
                  className="pt-1 pb-2 flex justify-center text-litlingo-primary font-bold bg-white rounded-b hover:underline"
                >
                  View all notifications
                </LinkLookup>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsCenter;
